import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Play, Pause, RotateCcw, Timer, Dumbbell } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const EXERCISES = [
  {
    id: 'star-jumps',
    name: 'Star Jumps',
    description: 'Jump with arms and legs spread wide',
    animation: 'starJumps'
  },
  {
    id: 'tuck-jumps',
    name: 'Tuck Jumps',
    description: 'Jump and bring knees to chest',
    animation: 'tuckJumps'
  },
  {
    id: 'press-ups',
    name: 'Press Ups',
    description: 'Push up from the ground',
    animation: 'pressUps'
  },
  {
    id: 'sit-ups',
    name: 'Sit Ups',
    description: 'Curl up from lying position',
    animation: 'sitUps'
  },
  {
    id: 'jogging',
    name: 'Jogging on the Spot',
    description: 'Run in place, lift those knees!',
    animation: 'jogging'
  },
  {
    id: 'squats',
    name: 'Squats',
    description: 'Bend knees, keep back straight',
    animation: 'squats'
  },
  {
    id: 'high-knees',
    name: 'High Knees',
    description: 'Bring knees up high while running',
    animation: 'highKnees'
  },
  {
    id: 'burpees',
    name: 'Burpees',
    description: 'Drop, push up, jump up!',
    animation: 'burpees'
  }
];

const DURATION_OPTIONS = [
  { label: '30 seconds', value: 30 },
  { label: '1 minute', value: 60 },
  { label: '2 minutes', value: 120 }
];

const EXERCISE_DURATION = 10; // Each exercise lasts 10 seconds

// Stick Figure Component with animations
const StickFigure = ({ animation, isActive }) => {
  const [frame, setFrame] = useState(0);
  
  useEffect(() => {
    if (!isActive) return;
    const interval = setInterval(() => {
      setFrame(f => (f + 1) % 2);
    }, 500);
    return () => clearInterval(interval);
  }, [isActive]);

  const getStickFigure = () => {
    switch (animation) {
      case 'starJumps':
        return frame === 0 ? (
          // Standing position
          <svg viewBox="0 0 100 150" className="w-full h-full">
            <circle cx="50" cy="20" r="15" fill="none" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="35" x2="50" y2="80" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="50" x2="30" y2="70" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="50" x2="70" y2="70" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="80" x2="35" y2="120" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="80" x2="65" y2="120" stroke="currentColor" strokeWidth="3"/>
          </svg>
        ) : (
          // Star position
          <svg viewBox="0 0 100 150" className="w-full h-full">
            <circle cx="50" cy="20" r="15" fill="none" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="35" x2="50" y2="80" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="45" x2="15" y2="30" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="45" x2="85" y2="30" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="80" x2="20" y2="120" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="80" x2="80" y2="120" stroke="currentColor" strokeWidth="3"/>
          </svg>
        );
      
      case 'tuckJumps':
        return frame === 0 ? (
          // Standing
          <svg viewBox="0 0 100 150" className="w-full h-full">
            <circle cx="50" cy="30" r="15" fill="none" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="45" x2="50" y2="90" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="55" x2="30" y2="75" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="55" x2="70" y2="75" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="90" x2="35" y2="130" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="90" x2="65" y2="130" stroke="currentColor" strokeWidth="3"/>
          </svg>
        ) : (
          // Tucked in air
          <svg viewBox="0 0 100 150" className="w-full h-full">
            <circle cx="50" cy="25" r="15" fill="none" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="40" x2="50" y2="70" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="50" x2="30" y2="40" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="50" x2="70" y2="40" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="70" x2="35" y2="55" stroke="currentColor" strokeWidth="3"/>
            <line x1="35" y1="55" x2="30" y2="75" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="70" x2="65" y2="55" stroke="currentColor" strokeWidth="3"/>
            <line x1="65" y1="55" x2="70" y2="75" stroke="currentColor" strokeWidth="3"/>
          </svg>
        );

      case 'pressUps':
        return frame === 0 ? (
          // Up position
          <svg viewBox="0 0 150 100" className="w-full h-full">
            <circle cx="30" cy="30" r="12" fill="none" stroke="currentColor" strokeWidth="3"/>
            <line x1="42" y1="30" x2="100" y2="35" stroke="currentColor" strokeWidth="3"/>
            <line x1="42" y1="30" x2="30" y2="55" stroke="currentColor" strokeWidth="3"/>
            <line x1="30" y1="55" x2="30" y2="75" stroke="currentColor" strokeWidth="3"/>
            <line x1="100" y1="35" x2="100" y2="55" stroke="currentColor" strokeWidth="3"/>
            <line x1="100" y1="55" x2="100" y2="75" stroke="currentColor" strokeWidth="3"/>
          </svg>
        ) : (
          // Down position
          <svg viewBox="0 0 150 100" className="w-full h-full">
            <circle cx="30" cy="50" r="12" fill="none" stroke="currentColor" strokeWidth="3"/>
            <line x1="42" y1="50" x2="100" y2="55" stroke="currentColor" strokeWidth="3"/>
            <line x1="38" y1="55" x2="20" y2="45" stroke="currentColor" strokeWidth="3"/>
            <line x1="20" y1="45" x2="25" y2="70" stroke="currentColor" strokeWidth="3"/>
            <line x1="100" y1="55" x2="118" y2="45" stroke="currentColor" strokeWidth="3"/>
            <line x1="100" y1="55" x2="100" y2="75" stroke="currentColor" strokeWidth="3"/>
          </svg>
        );

      case 'sitUps':
        return frame === 0 ? (
          // Lying down
          <svg viewBox="0 0 150 100" className="w-full h-full">
            <circle cx="25" cy="50" r="12" fill="none" stroke="currentColor" strokeWidth="3"/>
            <line x1="37" y1="50" x2="90" y2="50" stroke="currentColor" strokeWidth="3"/>
            <line x1="35" y1="50" x2="25" y2="35" stroke="currentColor" strokeWidth="3"/>
            <line x1="90" y1="50" x2="110" y2="30" stroke="currentColor" strokeWidth="3"/>
            <line x1="110" y1="30" x2="120" y2="50" stroke="currentColor" strokeWidth="3"/>
          </svg>
        ) : (
          // Sitting up
          <svg viewBox="0 0 150 100" className="w-full h-full">
            <circle cx="50" cy="25" r="12" fill="none" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="37" x2="70" y2="65" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="45" x2="35" y2="35" stroke="currentColor" strokeWidth="3"/>
            <line x1="70" y1="65" x2="100" y2="65" stroke="currentColor" strokeWidth="3"/>
            <line x1="100" y1="65" x2="115" y2="45" stroke="currentColor" strokeWidth="3"/>
            <line x1="115" y1="45" x2="125" y2="65" stroke="currentColor" strokeWidth="3"/>
          </svg>
        );

      case 'jogging':
        return frame === 0 ? (
          // Left leg up
          <svg viewBox="0 0 100 150" className="w-full h-full">
            <circle cx="50" cy="20" r="15" fill="none" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="35" x2="50" y2="80" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="50" x2="30" y2="60" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="50" x2="70" y2="40" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="80" x2="35" y2="70" stroke="currentColor" strokeWidth="3"/>
            <line x1="35" y1="70" x2="30" y2="95" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="80" x2="60" y2="120" stroke="currentColor" strokeWidth="3"/>
          </svg>
        ) : (
          // Right leg up
          <svg viewBox="0 0 100 150" className="w-full h-full">
            <circle cx="50" cy="20" r="15" fill="none" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="35" x2="50" y2="80" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="50" x2="70" y2="60" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="50" x2="30" y2="40" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="80" x2="65" y2="70" stroke="currentColor" strokeWidth="3"/>
            <line x1="65" y1="70" x2="70" y2="95" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="80" x2="40" y2="120" stroke="currentColor" strokeWidth="3"/>
          </svg>
        );

      case 'squats':
        return frame === 0 ? (
          // Standing
          <svg viewBox="0 0 100 150" className="w-full h-full">
            <circle cx="50" cy="20" r="15" fill="none" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="35" x2="50" y2="80" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="50" x2="30" y2="70" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="50" x2="70" y2="70" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="80" x2="35" y2="120" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="80" x2="65" y2="120" stroke="currentColor" strokeWidth="3"/>
          </svg>
        ) : (
          // Squatting
          <svg viewBox="0 0 100 150" className="w-full h-full">
            <circle cx="50" cy="35" r="15" fill="none" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="50" x2="50" y2="80" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="60" x2="25" y2="55" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="60" x2="75" y2="55" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="80" x2="30" y2="90" stroke="currentColor" strokeWidth="3"/>
            <line x1="30" y1="90" x2="25" y2="120" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="80" x2="70" y2="90" stroke="currentColor" strokeWidth="3"/>
            <line x1="70" y1="90" x2="75" y2="120" stroke="currentColor" strokeWidth="3"/>
          </svg>
        );

      case 'highKnees':
        return frame === 0 ? (
          // Left knee up high
          <svg viewBox="0 0 100 150" className="w-full h-full">
            <circle cx="50" cy="20" r="15" fill="none" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="35" x2="50" y2="80" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="50" x2="30" y2="55" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="50" x2="70" y2="45" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="80" x2="35" y2="60" stroke="currentColor" strokeWidth="3"/>
            <line x1="35" y1="60" x2="30" y2="90" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="80" x2="60" y2="120" stroke="currentColor" strokeWidth="3"/>
          </svg>
        ) : (
          // Right knee up high
          <svg viewBox="0 0 100 150" className="w-full h-full">
            <circle cx="50" cy="20" r="15" fill="none" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="35" x2="50" y2="80" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="50" x2="70" y2="55" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="50" x2="30" y2="45" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="80" x2="65" y2="60" stroke="currentColor" strokeWidth="3"/>
            <line x1="65" y1="60" x2="70" y2="90" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="80" x2="40" y2="120" stroke="currentColor" strokeWidth="3"/>
          </svg>
        );

      case 'burpees':
        return frame === 0 ? (
          // Standing with arms up
          <svg viewBox="0 0 100 150" className="w-full h-full">
            <circle cx="50" cy="25" r="15" fill="none" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="40" x2="50" y2="85" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="50" x2="35" y2="20" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="50" x2="65" y2="20" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="85" x2="35" y2="125" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="85" x2="65" y2="125" stroke="currentColor" strokeWidth="3"/>
          </svg>
        ) : (
          // Plank position
          <svg viewBox="0 0 100 150" className="w-full h-full">
            <g transform="translate(0, 41.665) scale(0.6667)">
              <circle cx="30" cy="35" r="12" fill="none" stroke="currentColor" strokeWidth="3"/>
              <line x1="42" y1="35" x2="110" y2="40" stroke="currentColor" strokeWidth="3"/>
              <line x1="40" y1="40" x2="30" y2="60" stroke="currentColor" strokeWidth="3"/>
              <line x1="30" y1="60" x2="30" y2="80" stroke="currentColor" strokeWidth="3"/>
              <line x1="110" y1="40" x2="110" y2="60" stroke="currentColor" strokeWidth="3"/>
              <line x1="110" y1="60" x2="110" y2="80" stroke="currentColor" strokeWidth="3"/>
            </g>
          </svg>
        );

      default:
        return (
          <svg viewBox="0 0 100 150" className="w-full h-full">
            <circle cx="50" cy="20" r="15" fill="none" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="35" x2="50" y2="80" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="50" x2="30" y2="70" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="50" x2="70" y2="70" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="80" x2="35" y2="120" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="80" x2="65" y2="120" stroke="currentColor" strokeWidth="3"/>
          </svg>
        );
    }
  };

  return (
    <div className="w-48 h-48 text-orange-600">
      {getStickFigure()}
    </div>
  );
};

const ExerciseExpert = () => {
  const navigate = useNavigate();
  const [selectedDuration, setSelectedDuration] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [exerciseTimer, setExerciseTimer] = useState(EXERCISE_DURATION);
  const [totalTimeRemaining, setTotalTimeRemaining] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const intervalRef = useRef(null);

  const currentExercise = EXERCISES[currentExerciseIndex % EXERCISES.length];

  useEffect(() => {
    if (isRunning && !isPaused) {
      intervalRef.current = setInterval(() => {
        setExerciseTimer(prev => {
          if (prev <= 1) {
            // Move to next exercise
            setCurrentExerciseIndex(i => i + 1);
            return EXERCISE_DURATION;
          }
          return prev - 1;
        });

        setTotalTimeRemaining(prev => {
          if (prev <= 1) {
            setIsRunning(false);
            setIsComplete(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isRunning, isPaused]);

  const startWorkout = (duration) => {
    setSelectedDuration(duration);
    setTotalTimeRemaining(duration);
    setExerciseTimer(EXERCISE_DURATION);
    setCurrentExerciseIndex(0);
    setIsRunning(true);
    setIsPaused(false);
    setIsComplete(false);
  };

  const togglePause = () => {
    setIsPaused(!isPaused);
  };

  const resetWorkout = () => {
    setIsRunning(false);
    setIsPaused(false);
    setSelectedDuration(null);
    setCurrentExerciseIndex(0);
    setExerciseTimer(EXERCISE_DURATION);
    setTotalTimeRemaining(0);
    setIsComplete(false);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFE0B2] via-[#FFCC80] to-[#FFB74D] relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-orange-500 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-red-400 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-6 py-8">
        <header className="flex justify-between items-center mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-white text-[#2D3748] px-6 py-3 rounded-full font-semibold hover:bg-slate-100 transition-all duration-300 flex items-center gap-2 shadow-sm"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
          
          <h1 className="text-3xl md:text-4xl font-bold text-[#2D3748] flex items-center gap-3">
            <Dumbbell className="w-8 h-8" />
            Exercise Expert
          </h1>
          
          <button
            onClick={resetWorkout}
            className="bg-white text-[#4A5568] px-6 py-3 rounded-full font-semibold hover:bg-slate-100 transition-all duration-300 flex items-center gap-2 shadow-sm"
          >
            <RotateCcw className="w-5 h-5" />
            Reset
          </button>
        </header>

        {!isRunning && !isComplete && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 mb-8">
              <p className="text-lg text-[#4A5568]">
                Follow along with on-the-spot exercises! Each exercise lasts 10 seconds.
              </p>
            </div>

            <h2 className="text-2xl font-bold text-[#2D3748] mb-6">Choose Your Workout Duration</h2>
            
            <div className="flex flex-wrap justify-center gap-4">
              {DURATION_OPTIONS.map(option => (
                <button
                  key={option.value}
                  onClick={() => startWorkout(option.value)}
                  className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-8 py-4 rounded-2xl font-bold text-lg hover:-translate-y-1 transition-all duration-300 shadow-lg flex items-center gap-3"
                >
                  <Timer className="w-6 h-6" />
                  {option.label}
                </button>
              ))}
            </div>

            {/* Preview exercises */}
            <div className="mt-12">
              <h3 className="text-xl font-semibold text-[#2D3748] mb-4">Exercises Include:</h3>
              <div className="flex flex-wrap justify-center gap-3">
                {EXERCISES.map(ex => (
                  <span key={ex.id} className="bg-white/80 px-4 py-2 rounded-full text-[#4A5568] text-sm">
                    {ex.name}
                  </span>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {isRunning && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center"
          >
            {/* Total time remaining */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl px-8 py-4 inline-block mb-6">
              <p className="text-sm text-[#4A5568]">Total Time Remaining</p>
              <p className="text-3xl font-bold text-[#2D3748]">{formatTime(totalTimeRemaining)}</p>
            </div>

            {/* Current exercise */}
            <div className="bg-white rounded-3xl p-8 shadow-xl mb-8">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentExercise.id}
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -50 }}
                >
                  <h2 className="text-3xl font-bold text-[#2D3748] mb-2">{currentExercise.name}</h2>
                  <p className="text-lg text-[#4A5568] mb-6">{currentExercise.description}</p>
                  
                  {/* Stick figure animation */}
                  <div className="flex justify-center mb-6">
                    <StickFigure animation={currentExercise.animation} isActive={!isPaused} />
                  </div>

                  {/* Exercise timer */}
                  <div className="relative w-32 h-32 mx-auto mb-4">
                    <svg className="w-full h-full transform -rotate-90">
                      <circle
                        cx="64"
                        cy="64"
                        r="56"
                        fill="none"
                        stroke="#E5E7EB"
                        strokeWidth="8"
                      />
                      <circle
                        cx="64"
                        cy="64"
                        r="56"
                        fill="none"
                        stroke="#F97316"
                        strokeWidth="8"
                        strokeLinecap="round"
                        strokeDasharray={2 * Math.PI * 56}
                        strokeDashoffset={2 * Math.PI * 56 * (1 - exerciseTimer / EXERCISE_DURATION)}
                        className="transition-all duration-300"
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-4xl font-bold text-[#2D3748]">{exerciseTimer}</span>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Controls */}
            <div className="flex justify-center gap-4">
              <button
                onClick={togglePause}
                className={`px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 shadow-lg flex items-center gap-2 ${
                  isPaused 
                    ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white' 
                    : 'bg-gradient-to-r from-amber-500 to-orange-500 text-white'
                }`}
              >
                {isPaused ? <Play className="w-6 h-6" /> : <Pause className="w-6 h-6" />}
                {isPaused ? 'Resume' : 'Pause'}
              </button>
            </div>

            {/* Exercise queue */}
            <div className="mt-8 bg-white/60 backdrop-blur-sm rounded-2xl p-4">
              <p className="text-sm text-[#4A5568] mb-2">Coming up next:</p>
              <div className="flex justify-center gap-2 flex-wrap">
                {[1, 2, 3].map(offset => {
                  const nextEx = EXERCISES[(currentExerciseIndex + offset) % EXERCISES.length];
                  return (
                    <span key={offset} className="bg-white px-3 py-1 rounded-full text-sm text-[#4A5568]">
                      {nextEx.name}
                    </span>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}

        {isComplete && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center"
          >
            <div className="bg-white rounded-3xl p-12 shadow-xl">
              <div className="text-6xl mb-4">🎉</div>
              <h2 className="text-3xl font-bold text-[#2D3748] mb-2">Workout Complete!</h2>
              <p className="text-lg text-[#4A5568] mb-2">
                Amazing work! You completed {formatTime(selectedDuration)} of exercise!
              </p>
              <p className="text-[#4A5568] mb-8">
                You did {Math.ceil(selectedDuration / EXERCISE_DURATION)} exercises!
              </p>
              
              <button
                onClick={resetWorkout}
                className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-8 py-4 rounded-full font-bold text-lg hover:-translate-y-1 transition-all duration-300 shadow-lg"
              >
                Start Another Workout
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default ExerciseExpert;
